import React from 'react';
import { X, Smartphone, Monitor, ExternalLink } from 'lucide-react';

interface PublishInstructionsProps {
  shopName: string;
  onClose: () => void;
}

const PublishInstructions: React.FC<PublishInstructionsProps> = ({ 
  shopName, 
  onClose 
}) => {
  const googleMapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(shopName)}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">
            Google Maps投稿手順
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-6 space-y-6">
          {/* デバイス選択 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Smartphone className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="font-semibold text-green-800">スマートフォン（推奨）</h3>
              </div>
              <p className="text-sm text-green-700">
                アプリから簡単に投稿できます
              </p>
            </div>
            <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Monitor className="h-5 w-5 text-gray-600 mr-2" />
                <h3 className="font-semibold text-gray-800">パソコン</h3>
              </div>
              <p className="text-sm text-gray-700">
                ブラウザからも投稿可能です
              </p>
            </div>
          </div>

          {/* スマートフォン手順 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
              <Smartphone className="h-5 w-5 mr-2" />
              スマートフォンでの投稿手順
            </h3>
            <ol className="space-y-3">
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  1
                </span>
                <div>
                  <p className="font-medium">Google Mapsアプリを開く</p>
                  <p className="text-sm text-blue-700">アプリがない場合はApp Store/Google Playからダウンロード</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  2
                </span>
                <div>
                  <p className="font-medium">「{shopName}」を検索</p>
                  <p className="text-sm text-blue-700">検索窓に店舗名を入力してください</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  3
                </span>
                <div>
                  <p className="font-medium">店舗ページで「口コミ」をタップ</p>
                  <p className="text-sm text-blue-700">店舗情報の下部にある口コミセクションを探してください</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  4
                </span>
                <div>
                  <p className="font-medium">「口コミを書く」をタップ</p>
                  <p className="text-sm text-blue-700">星マークの付いたボタンを押してください</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  5
                </span>
                <div>
                  <p className="font-medium">星評価を選択</p>
                  <p className="text-sm text-blue-700">1〜5つ星で評価を選んでください</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  6
                </span>
                <div>
                  <p className="font-medium">下書きをコピー＆ペースト</p>
                  <p className="text-sm text-blue-700">編集画面の下書き内容をコピーして貼り付けてください</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm font-medium">
                  7
                </span>
                <div>
                  <p className="font-medium">「投稿」ボタンをタップ</p>
                  <p className="text-sm text-blue-700">内容を確認して投稿を完了してください</p>
                </div>
              </li>
            </ol>
          </div>

          {/* パソコン手順 */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <Monitor className="h-5 w-5 mr-2" />
              パソコンでの投稿手順
            </h3>
            <ol className="space-y-2 text-sm">
              <li>1. ブラウザでGoogle Mapsを開く</li>
              <li>2. 「{shopName}」を検索</li>
              <li>3. 店舗ページで「レビューを書く」をクリック</li>
              <li>4. 星評価を選択し、下書きを貼り付け</li>
              <li>5. 「投稿」ボタンをクリック</li>
            </ol>
          </div>

          {/* 注意事項 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-yellow-800 mb-2">
              ⚠️ 投稿時の注意点
            </h3>
            <ul className="space-y-1 text-sm text-yellow-700">
              <li>• Googleアカウントにログインが必要です</li>
              <li>• 投稿後の編集・削除はGoogle Maps上で行ってください</li>
              <li>• 個人情報（電話番号、住所など）は含めないでください</li>
              <li>• 投稿は完全にお客様の判断で行ってください</li>
            </ul>
          </div>

          {/* アクションボタン */}
          <div className="flex space-x-3">
            <button
              onClick={() => window.open(googleMapsUrl, '_blank')}
              className="flex-1 bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 transition-colors font-medium flex items-center justify-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Google Mapsを開く
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              閉じる
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishInstructions;